import { PageHeader } from "@finitless/shared/components";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getTimezoneFromLocation,
  getDefaultSchedule,
} from "@finitless/shared/lib";

import {
  Button,
  Icon,
  Input,
  CustomGoogleAutocompleteInput,
} from "../../../ui";
import Breadcrumb from "../../../ui/Navigation/Breadcrumb";
import SimpleMap from "../../../ui/UIElements/SimpleMap";
import { useBusinessCreation, useGoogleMaps } from "../../../../hooks";
import {
  PaymentMethodsStep,
  PhotosStep,
  ScheduleStep,
  validateStep,
  validateEntireForm,
} from "../../../ui/Business";
import "../../../catalog-ordering.css";

const ERROR_FIELD_STEPS = {
  name: 1,
  email: 1,
  phone: 1,
  address: 2,
  location: 2,
  timezone: 2,
  price_level: 3,
  paymentMethods: 3,
  schedule: 4,
};

const BusinessCreationForm = () => {
  const { t } = useTranslation("stores");
  const navigate = useNavigate();
  const location = useLocation();
  const {
    formData,
    updateField,
    updateNestedField,
    updateName,
    createBusiness,
    isLoading,
  } = useBusinessCreation();

  const [currentStep, setCurrentStep] = useState(1);
  const [formErrors, setFormErrors] = useState({});
  const [pendingErrorFocus, setPendingErrorFocus] = useState(null);
  const stepContentRef = useRef(null);
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [schedule, setSchedule] = useState([]);

  // Google Maps API key
  const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  // Check if Google Maps is loaded
  useGoogleMaps();

  // Initialize form with place data if available
  useEffect(() => {
    if (location.state?.placeData) {
      const placeData = location.state.placeData;

      // Pre-fill data from Google Maps place
      if (placeData.name) updateName(placeData.name);
      if (placeData.address) updateField("address", placeData.address);
      if (placeData.phone) {
        // Extract only numbers from phone
        const numericPhone = placeData.phone.replace(/\D/g, "");
        updateField("cellphone", numericPhone);
      }
      if (placeData.price_level)
        updateField("price_level", placeData.price_level.toString());
      if (placeData.location) {
        updateNestedField("location", "lat", placeData.location.lat);
        updateNestedField("location", "lng", placeData.location.lng);
        updateNestedField(
          "location",
          "zipcode",
          placeData.address_components?.zipcode || -1,
        );
      }
      if (placeData.timezone) updateField("timezone", placeData.timezone);
    }
  }, [location.state?.placeData, updateField, updateNestedField]);

  const steps = [
    {
      id: 1,
      title: t("creationForm.steps.basicInfo.title"),
      description: t("creationForm.steps.basicInfo.description"),
    },
    {
      id: 2,
      title: t("creationForm.steps.address.title"),
      description: t("creationForm.steps.address.description"),
    },
    {
      id: 3,
      title: t("creationForm.steps.paymentMethods.title"),
      description: t("creationForm.steps.paymentMethods.description"),
    },
    {
      id: 4,
      title: t("creationForm.steps.schedule.title"),
      description: t("creationForm.steps.schedule.description"),
    },
    {
      id: 5,
      title: t("creationForm.steps.logo.title"),
      description: t("creationForm.steps.logo.description"),
    },
    {
      id: 6,
      title: t("creationForm.steps.review.title"),
      description: t("creationForm.steps.review.description"),
    },
  ];

  const handleFieldChange = (field, value) => {
    if (field === "name") {
      updateName(value);
    } else {
      updateField(field, value);
    }
  };

  // Handle price level change
  const handlePriceLevelChange = (value) => {
    updateField("price_level", value);
  };

  // Handle phone number input - only allow numbers and max 15 digits
  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;

    // Only allow numbers
    const numericValue = value.replace(/\D/g, "");

    // Limit to 15 digits
    if (numericValue.length <= 15) {
      updateField("cellphone", numericValue);
    }
  };

  const handleGoogleAddressSelection = async (addressData) => {
    if (addressData?.map_data?.place_id) {
      // Update address
      updateField("address", addressData.address);

      // Update location coordinates
      if (addressData.location) {
        updateNestedField("location", "lat", addressData.location.lat);
        updateNestedField("location", "lng", addressData.location.lng);
        updateNestedField("location", "zipcode", addressData.zipcode || -1);

        // Auto-populate timezone from coordinates using Google Maps Time Zone API
        try {
          if (GOOGLE_MAPS_API_KEY) {
            const timezone = await getTimezoneFromLocation(
              addressData.location.lat,
              addressData.location.lng,
              GOOGLE_MAPS_API_KEY,
            );

            if (timezone) {
              updateField("timezone", timezone);
              // Clear timezone error if it was set
              if (formErrors.timezone) {
                setFormErrors((prev) => ({
                  ...prev,
                  timezone: "",
                }));
              }
            } else {
              console.warn("No timezone found for location");
            }
          } else {
            console.warn(
              "Google Maps API key not available for timezone detection",
            );
          }
        } catch (error) {
          console.error("Error fetching timezone:", error);
        }
      }

      // Update timezone if available from address data (fallback)
      if (addressData.timezone) {
        updateField("timezone", addressData.timezone);
      }

      // Clear any location errors
      if (formErrors.location) {
        setFormErrors((prev) => ({
          ...prev,
          location: "",
        }));
      }
    }
  };

  // Handle payment method selection
  const handlePaymentMethodToggle = (methodId) => {
    setSelectedPaymentMethods((prev) =>
      prev.includes(methodId)
        ? prev.filter((id) => id !== methodId)
        : [...prev, methodId],
    );
  };

  // Handle file uploads
  const handleLogoUpload = (file) => {
    setLogoFile(file);
  };

  const handleLogoRemove = () => {
    setLogoFile(null);
  };

  // Handle schedule changes
  const handleScheduleChange = useCallback(
    (newSchedule) => {
      setSchedule(newSchedule);
      updateField("schedule", newSchedule);
    },
    [updateField],
  );

  // Validate current step and set errors
  const validateCurrentStep = (step) => {
    const errors = validateStep(
      step,
      formData,
      selectedPaymentMethods,
      schedule,
    );
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validate entire form and set errors
  const validateAllSteps = () => {
    const allErrors = validateEntireForm(
      formData,
      selectedPaymentMethods,
      schedule,
    );
    setFormErrors(allErrors);
    return allErrors;
  };

  const handleNext = () => {
    // Validate current step
    const isValid = validateCurrentStep(currentStep);

    if (isValid) {
      // If moving past schedule step and no schedule set, apply default schedule
      if (currentStep === 4 && (!schedule || schedule.length === 0)) {
        const defaultSched = getDefaultSchedule();
        setSchedule(defaultSched);
        updateField("schedule", defaultSched);
      }

      // Clear errors before advancing
      setFormErrors({});
      setCurrentStep((prev) => Math.min(prev + 1, steps.length));
    }
    // Si hay errores, no avanza y los errores ya se muestran en validateCurrentStep
  };

  const handlePrevious = () => {
    // Clear errors when going back
    setFormErrors({});
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    // Validate entire form before submission
    const errors = validateAllSteps();
    if (Object.keys(errors).length === 0) {
      // Prepare additional data to send to API
      const additionalData = {
        // Pass files with base64 data
        logo: logoFile,
        payment_methods: selectedPaymentMethods,
      };

      // Remove null values
      Object.keys(additionalData).forEach((key) => {
        if (additionalData[key] === null || additionalData[key] === undefined) {
          delete additionalData[key];
        }
      });

      const result = await createBusiness(additionalData);
      if (result.success) {
        navigate("/menu/stores");
      } else {
        // Error is already handled by the hook
        console.error("Business creation failed:", result.error);
      }
    } else {
      const firstErrorField = Object.keys(errors)[0];
      const errorStep = ERROR_FIELD_STEPS[firstErrorField];
      if (errorStep) {
        setCurrentStep(errorStep);
        setPendingErrorFocus({ field: firstErrorField, step: errorStep });
      }
    }
  };

  useEffect(() => {
    if (!pendingErrorFocus || currentStep !== pendingErrorFocus.step) return;
    // Location and timezone are corrected through the address picker;
    // the derived timezone input is deliberately disabled.
    const field = ["location", "timezone"].includes(pendingErrorFocus.field)
      ? "address"
      : pendingErrorFocus.field;
    const fieldContainer = stepContentRef.current?.querySelector(
      `[data-field="${field}"]`,
    );
    const control = fieldContainer?.querySelector(
      'input:not(:disabled), select:not(:disabled), textarea:not(:disabled), button:not(:disabled), [tabindex="0"]',
    );
    if (control) {
      control.scrollIntoView({ behavior: "smooth", block: "center" });
      control.focus();
    }
    setPendingErrorFocus(null);
  }, [currentStep, pendingErrorFocus]);

  const handleBack = () => {
    navigate("/menu/stores");
  };

  const breadcrumbItems = [
    { label: t("creationForm.breadcrumb.stores"), path: "/menu/stores" },
    {
      label: t("creationForm.breadcrumb.createStore"),
      path: "/menu/stores/create",
    },
    {
      label: t("creationForm.breadcrumb.storeDetails"),
      path: null,
      active: true,
    },
  ];

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4 sm:space-y-6">
            <div data-field="name">
              <label className="text-foreground mb-2 block text-sm font-medium">
                {t("creationForm.fields.businessName.label")}
                <span className="text-destructive ml-1">*</span>
              </label>
              <Input
                value={formData.name}
                onChange={(e) => handleFieldChange("name", e.target.value)}
                placeholder={t("creationForm.fields.businessName.placeholder")}
                variant="dark"
                size="lg"
                error={formErrors.name}
              />
            </div>

            <div>
              <Input
                label={t("creationForm.fields.slug.label")}
                value={formData.slug}
                onChange={(e) => handleFieldChange("slug", e.target.value)}
                placeholder={t("creationForm.fields.slug.placeholder")}
                variant="dark"
                size="lg"
                helperText={t("creationForm.fields.slug.helper")}
              />
            </div>

            <div data-field="email">
              <label className="text-foreground mb-2 block text-sm font-medium">
                {t("creationForm.fields.email.label")}
                <span className="text-destructive ml-1">*</span>
              </label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleFieldChange("email", e.target.value)}
                placeholder={t("creationForm.fields.email.placeholder")}
                variant="dark"
                size="lg"
                error={formErrors.email}
              />
            </div>

            <div data-field="phone">
              <label className="text-foreground mb-2 block text-sm font-medium">
                {t("creationForm.fields.phone.label")}
                <span className="text-destructive ml-1">*</span>
              </label>
              <Input
                type="text"
                value={formData.cellphone || ""}
                onChange={handlePhoneNumberChange}
                placeholder={t("creationForm.fields.phone.placeholder")}
                variant="dark"
                size="lg"
                error={formErrors.phone}
              />
              <p className="text-muted-foreground mt-1 text-xs">
                {t("creationForm.fields.phone.helper")}
              </p>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4 sm:space-y-6">
            <div data-field="address">
              <label className="text-foreground mb-2 block text-sm font-medium">
                {t("creationForm.fields.address.label")}
                <span className="text-destructive ml-1">*</span>
              </label>
              <div className="relative">
                <CustomGoogleAutocompleteInput
                  apiKey={GOOGLE_MAPS_API_KEY}
                  value={formData.address}
                  onChangeAddress={handleGoogleAddressSelection}
                  placeholder={t("creationForm.fields.address.placeholder")}
                  types={["address"]}
                  fields={[
                    "place_id",
                    "formatted_address",
                    "geometry",
                    "address_components",
                  ]}
                  countryCode="*"
                  className={`bg-secondary focus:ring-ring h-10 w-full rounded-lg border px-3 py-2 text-sm text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:outline-none sm:h-12 sm:px-4 sm:py-3 sm:text-base ${
                    formErrors.address ? "border-destructive" : "border-border"
                  }`}
                />
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <Icon
                    name="MapPin"
                    className="text-muted-foreground h-4 w-4 sm:h-5 sm:w-5"
                  />
                </div>
              </div>
              <p className="text-muted-foreground mt-2 text-xs sm:text-sm">
                {t("creationForm.fields.address.helper")}
              </p>
              {formErrors.address && (
                <p className="text-destructive mt-2 text-xs sm:text-sm">
                  {formErrors.address}
                </p>
              )}
            </div>

            {/* Map Display */}
            {formData.location?.lat && formData.location?.lng && (
              <div>
                <label className="text-foreground mb-2 block text-sm font-medium">
                  {t("creationForm.fields.locationPreview.label")}
                </label>
                <div className="border-border bg-secondary relative h-48 w-full overflow-hidden rounded-lg border sm:h-64">
                  <SimpleMap
                    lat={formData.location.lat}
                    lng={formData.location.lng}
                    zoom={15}
                  />
                  <div className="bg-opacity-50 absolute top-2 right-2 z-10 rounded bg-black px-2 py-1 text-xs text-white">
                    {formData.location.lat.toFixed(6)},{" "}
                    {formData.location.lng.toFixed(6)}
                  </div>
                </div>
                <p className="text-muted-foreground mt-2 text-xs sm:text-sm">
                  {t("creationForm.fields.locationPreview.helper")}
                </p>
              </div>
            )}

            <div data-field="timezone">
              <label className="text-foreground mb-2 block text-sm font-medium">
                {t("creationForm.fields.timezone.label")}
                <span className="text-destructive ml-1">*</span>
              </label>
              <Input
                value={formData.timezone}
                placeholder={t("creationForm.fields.timezone.placeholder")}
                variant="dark"
                size="lg"
                error={formErrors.timezone}
                disabled={true}
                helperText={t("creationForm.fields.timezone.helper")}
              />
            </div>
          </div>
        );

      case 3:
        return (
          <PaymentMethodsStep
            selectedPaymentMethods={selectedPaymentMethods}
            onPaymentMethodToggle={handlePaymentMethodToggle}
            priceLevel={formData.price_level}
            onPriceLevelChange={handlePriceLevelChange}
            errors={formErrors}
          />
        );

      case 4:
        return (
          <div data-field="schedule">
            <ScheduleStep
              schedule={schedule}
              onScheduleChange={handleScheduleChange}
              errors={formErrors}
            />
          </div>
        );

      case 5:
        return (
          <PhotosStep
            logoFile={logoFile}
            onLogoUpload={handleLogoUpload}
            onLogoRemove={handleLogoRemove}
          />
        );

      case 6:
        return (
          <div className="space-y-4 sm:space-y-6">
            <div className="bg-secondary rounded-lg p-4 sm:p-6">
              <h3 className="mb-3 text-base font-medium text-white sm:mb-4 sm:text-lg">
                {t("creationForm.review.title")}
              </h3>
              <div className="space-y-2 sm:space-y-3">
                <div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:gap-0">
                  <span className="text-muted-foreground text-xs sm:text-sm">
                    {t("creationForm.review.labels.name")}
                  </span>
                  <span className="text-xs text-white sm:text-sm">
                    {formData.name}
                  </span>
                </div>
                <div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:gap-0">
                  <span className="text-muted-foreground text-xs sm:text-sm">
                    {t("creationForm.review.labels.email")}
                  </span>
                  <span className="text-xs text-white sm:text-sm">
                    {formData.email}
                  </span>
                </div>
                <div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:gap-0">
                  <span className="text-muted-foreground text-xs sm:text-sm">
                    {t("creationForm.review.labels.address")}
                  </span>
                  <span className="text-xs break-words text-white sm:text-sm">
                    {formData.address}
                  </span>
                </div>
                <div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:gap-0">
                  <span className="text-muted-foreground text-xs sm:text-sm">
                    {t("creationForm.review.labels.phone")}
                  </span>
                  <span className="text-xs text-white sm:text-sm">
                    {formData.cellphone || t("creationForm.review.notProvided")}
                  </span>
                </div>
                <div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:gap-0">
                  <span className="text-muted-foreground text-xs sm:text-sm">
                    {t("creationForm.review.labels.timezone")}
                  </span>
                  <span className="text-xs text-white sm:text-sm">
                    {formData.timezone || t("creationForm.review.notProvided")}
                  </span>
                </div>
                <div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:gap-0">
                  <span className="text-muted-foreground text-xs sm:text-sm">
                    {t("creationForm.review.labels.priceLevel")}
                  </span>
                  <span className="text-xs text-white sm:text-sm">
                    {"$".repeat(parseInt(formData.price_level))}
                  </span>
                </div>
                <div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:gap-0">
                  <span className="text-muted-foreground text-xs sm:text-sm">
                    {t("creationForm.review.labels.paymentMethods")}
                  </span>
                  <span className="text-xs text-white sm:text-sm">
                    {selectedPaymentMethods.length > 0
                      ? `${selectedPaymentMethods.length} methods selected`
                      : t("creationForm.review.noneSelected")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className="ordering-catalog-surface space-y-4 sm:space-y-6"
      data-ordering-expression="product"
    >
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Main Content */}
      <div className="border-border bg-background rounded-lg border">
        {/* Header */}
        <div className="border-border border-b p-4 sm:p-6">
          <PageHeader
            title={t("creationForm.header.title")}
            subtitle={
              <>
                {t("creationForm.header.stepPrefix", {
                  current: currentStep,
                  total: steps.length,
                })}{" "}
                {steps[currentStep - 1].description}
              </>
            }
          />
        </div>

        {/* Progress Steps */}
        <div className="border-border border-b px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex items-center justify-between overflow-x-auto">
            {steps.map((step) => (
              <div key={step.id} className="flex flex-shrink-0 items-center">
                <div
                  className={`ordering-catalog-step-indicator flex h-6 w-6 items-center justify-center rounded-full border-2 sm:h-8 sm:w-8 ${
                    currentStep >= step.id
                      ? "border-primary bg-primary text-white"
                      : "border-border bg-secondary text-muted-foreground"
                  }`}
                >
                  {currentStep > step.id ? (
                    <Icon name="Check" className="h-3 w-3 sm:h-4 sm:w-4" />
                  ) : (
                    <span className="text-xs font-medium sm:text-sm">
                      {step.id}
                    </span>
                  )}
                </div>
                <div className="ml-2 hidden sm:ml-3 sm:block">
                  <div className="text-foreground text-xs font-medium sm:text-sm">
                    {step.title}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="p-4 sm:p-6">
          {/* Error Summary - Only show errors for current step */}
          {Object.keys(formErrors).length > 0 && (
            <div className="border-destructive bg-destructive/20 mb-4 rounded-lg border p-3 sm:mb-6 sm:p-4">
              <div className="flex items-start space-x-2 sm:space-x-3">
                <Icon
                  name="AlertCircle"
                  className="text-destructive mt-0.5 h-4 w-4 flex-shrink-0 sm:h-5 sm:w-5"
                />
                <div>
                  <h4 className="text-destructive mb-2 text-sm font-medium sm:text-base">
                    {t("creationForm.errors.summaryTitle")}
                  </h4>
                  <ul className="space-y-1">
                    {Object.entries(formErrors).map(([field, error]) => (
                      <li
                        key={field}
                        className="text-destructive text-xs sm:text-sm"
                      >
                        • {error}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div ref={stepContentRef}>{renderStepContent()}</div>

          {/* Action Buttons */}
          <div className="ordering-catalog-actions border-border mt-6 flex flex-col gap-3 border-t pt-6 sm:mt-8 sm:flex-row sm:items-center sm:justify-between sm:gap-0 sm:pt-8">
            <Button
              onClick={currentStep === 1 ? handleBack : handlePrevious}
              variant="outline"
              size="lg"
              className="border-border text-foreground hover:bg-secondary w-full sm:w-auto"
            >
              <Icon name="ArrowLeft" className="mr-2 h-4 w-4" />
              {currentStep === 1
                ? t("common:actions.cancel")
                : t("creationForm.actions.previous")}
            </Button>

            <div className="flex items-center space-x-3 sm:space-x-4">
              {currentStep < steps.length ? (
                <Button
                  variant="default"
                  onClick={handleNext}
                  size="lg"
                  className="w-full sm:w-auto"
                >
                  {t("creationForm.actions.next")}
                  <Icon name="ArrowRight" className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  onClick={handleSubmit}
                  isLoading={isLoading}
                  disabled={isLoading}
                  size="lg"
                  className="bg-success hover:bg-success/90 w-full text-white sm:w-auto"
                >
                  <Icon name="Check" className="mr-2 h-4 w-4" />
                  {t("creationForm.actions.createBusiness")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessCreationForm;
